/*global pushly*/
import { all, call, fork, put, select, take, takeEvery } from 'redux-saga/effects';
import { getJSON, putJSON } from '../../../shared/util-fetch';
import { MediaQuery as MQ } from '../../../shared/media-query';

// Helper function to get context
const getContext = () => {
    if (typeof window !== 'undefined' && window.location && window.location.href) {
        return window.location.href.includes('readings-reports') ? 'reading' : 'nav';
    }
    return 'nav';
};

export function* login({ payload }) { // eslint-disable-line complexity
    const devcat = typeof MQ.isMobile === 'function' && MQ.isMobile() ? 'Mobile' : 'Desktop';
    const fromSignup = payload && payload.fromSignup;
    const meth = payload && payload.sso ? payload.sso : (payload && payload.meth ? payload.meth : 'button');
    const event_name = fromSignup ? 'existing_account_login_bypass' : 'login_success';
    const signupIntent = yield select((state) => state['signup-intent']);
    const ctx = (signupIntent && signupIntent.ctx) ? signupIntent.ctx : getContext();

    try {
        if (payload && payload.sso) {
            let params = {
                credential: payload.credential,
                sso: payload.sso,
            };
            if (payload.user_id) {
                params.user_id = payload.user_id;
            }
            if (payload.email) {
                params.email = payload.email;
            }

            yield call(putJSON, '/api/user/login', params);

            yield put({
                type: 'ga:custom_event',
                payload: {
                    event_name: event_name,
                    params: {
                        devcat: devcat,
                        ctx: ctx,
                        meth: payload.sso,
                    },
                },
            });
        } else {
            yield call(putJSON, '/api/user/login', payload);

            yield put({
                type: 'ga:custom_event',
                payload: {
                    event_name: event_name,
                    params: {
                        devcat: devcat,
                        ctx: ctx,
                        meth: 'password',
                    },
                },
            });
        }
    } catch (e) {
        if (e.body && e.body.error_code) {
            const event_name = fromSignup ? 'existing_account_signup_error' : 'login_error';
            const shouldLogError = !fromSignup || e.body.error_code !== 'member not found';

            if (shouldLogError) {
                yield put({
                    type: 'ga:custom_event',
                    payload: {
                        event_name: event_name,
                        params: {
                            devcat: devcat,
                            meth: meth,
                            ctx: ctx,
                            error_type: JSON.stringify(e.body.error_code),
                        },
                    },
                });
            }

            if (e.body.error_code == 'member not found') {
                if (fromSignup) {
                    // From signup flow - proceed with signup
                    yield put({
                        type: 'user:update',
                        payload: {
                            email: payload.email,
                            password: payload.password,
                            proceedToSignup: true,
                        },
                    });

                    yield put({
                        type: 'ga:custom_event',
                        payload: {
                            event_name: 'sign_up_submit',
                            params: {
                                devcat: devcat,
                                ctx: ctx,
                                meth: meth,
                            },
                        },
                    });
                } else {
                    // Normal login flow - route to account-not-exists
                    try {
                        yield call(getJSON, '/api/user/validate-email', { email: payload.email });

                        yield all([
                            put({
                                type: 'ga:event',
                                payload: [
                                    'sign_up',
                                    'Modal_Login-Signup',
                                ],
                            }),
                            put({
                                type: 'user:update',
                                payload: {
                                    email: payload.email,
                                    password: payload.password,
                                },
                            }),
                            put({
                                type: 'route:to',
                                payload: 'account-not-exists',
                            }),
                        ]);
                    } catch (e) {
                        if (e.body.error.email && e.body.error.email == 'Email is required' && payload && payload.sso) {
                            const ssoName = payload.sso.charAt(0).toUpperCase() + payload.sso.slice(1);
                            yield put({
                                type: 'user:update',
                                payload: {
                                    emailError: 'Please enter a valid email - ' + ssoName + ' email not found',
                                },
                            });
                        } else {
                            yield put({
                                type: 'user:update',
                                payload: {
                                    emailError: e.body.error.email,
                                },
                            });
                        }
                    }
                }
            } else if (e.body.error_code == 'invalid password') {
                if (fromSignup) {
                    // From signup flow - route to login modal with email prefilled
                    yield put({
                        type: 'user:update',
                        payload: {
                            email: payload.email,
                            passwordError: true,
                        },
                    });

                    yield put({
                        type: 'route:to',
                        payload: 'login',
                    });
                } else {
                    // Normal login flow - show password error
                    yield put({
                        type: 'user:update',
                        payload: {
                            passwordError: true,
                        },
                    });
                }
            } else if (e.body.error_code == 'member disabled' || e.body.error == 'member disabled') {
                yield put({
                    type: 'user:update',
                    payload: {
                        passwordError: 'The account associated with this email address account has been deleted. ' +
                            'Please contact support@tarot.com for further assistance.',
                    },
                });
            }
        } else {
            yield put({
                type: 'ga:custom_event',
                payload: {
                    event_name: 'login_error',
                    params: {
                        devcat: devcat,
                        meth: meth,
                        ctx: ctx,
                        error_type: JSON.stringify(e),
                    },
                },
            });

            if (fromSignup && payload && payload.email) {
                // Route to login modal on other errors from signup
                yield put({
                    type: 'user:update',
                    payload: {
                        email: payload.email,
                        passwordError: e.body && e.body.message ? e.body.message : 'Login failed. Please try again.',
                    },
                });

                yield put({
                    type: 'route:to',
                    payload: 'login',
                });
            } else {
                yield put({
                    type: 'route:to',
                    payload: '',
                });
            }
        }
    }

    yield put({ type: 'user:refresh' });

    const { payload: user } = yield take('user:update');

    if (user && user.logged_in) {
        // Add birthdate and sign to pushly (no effect or error if not subscribed to pushly)
        pushly('profile', {
            birth_date: user.birth_date,
            sign: user.default_sign,
        });

        yield all([
            put({
                type: 'ga:event',
                payload: [
                    'sign_up',
                    'Modal_Login',
                ],
            }),
        ]);

        const pending = yield select(state => state.product.pending);
        if (pending) {
            yield put({
                type: 'product:update',
                payload: {
                    pending: false,
                },
            });
        }

        yield put({
            type: 'user:logged-in',
            payload: user,
        });

        yield put({
            type: 'ga:set-user',
            payload: user.member_uuid,
        });

        const route = yield select(state => state.route);
        const signupIntent = yield select(state => state['signup-intent']);

        if (signupIntent && signupIntent.onSuccess === 'navigate' && signupIntent.url) {
            yield put({ type: 'signup-intent:clear' });
            yield put({ type: 'location:href', payload: signupIntent.url });
        } else if (signupIntent && signupIntent.onSuccess === 'reload') {
            yield put({ type: 'signup-intent:clear' });
            window.location.reload();
        } else if (fromSignup || route === 'login' || route === 'signup' || route === 'login-signup') {
            yield put({
                type: 'route:to',
                payload: '',
            });
        }
    }
}

export function* logout() {
    yield call(putJSON, '/api/user/logout');
    yield put({ type: 'user:logged-out' });
}

export function* loggedOut() {
    const path = window.location.pathname;

    if (path === '/checkout') {
        yield put({ type: 'location:href', payload: '/readings-reports' });
    } else if (path.match(/^\/member\//)) {
        yield put({ type: 'location:href', payload: '/' });
    }
}

export function* logoutHomeLogin() {
    yield call(putJSON, '/api/user/logout');
    window.location.href = '/#login';
}

export function* loginViaPassword({ payload }) {
    try {
        if (payload && payload.sso) {
            let params = {
                credential: payload.credential,
                sso: payload.sso,
            };
            if (payload.user_id) {
                params.user_id = payload.user_id;
            }
            if (payload.email) {
                params.email = payload.email;
            }

            yield call(putJSON, '/api/user/login', params);
        } else {
            yield call(putJSON, '/api/user/login', payload);
        }
    } catch (e) {
        if (e.body && e.body.error_code && e.body.error_code == 'invalid password') {
            yield put({
                type: 'user:update',
                payload: {
                    passwordError: true,
                },
            });
        }
    }

    const user = yield select(state => state.user);

    if (user && !user.passwordError) {
        yield all([
            put({
                type: 'user:update',
                payload: {
                    auto_login: false,
                },
            }),
        ]);
    }
}

export function* watchLOLI() {
    while (true) {
        yield take(['user:logged-in', 'user:logged-out']);
        let user = yield select(state => state.user);

        const lolis = document.querySelectorAll('.is-lo, .is-li');

        if (lolis && lolis.length && user.logged_in) {
            lolis.forEach(loli => {
                loli.classList.remove('is-user-lo');
                loli.classList.add('is-user-li');
            });
        } else if (lolis && lolis.length && !user.logged_in) {
            lolis.forEach(loli => {
                loli.classList.remove('is-user-li');
                loli.classList.add('is-user-lo');
            });
        }

        // Update hero promo (carousel) buttons so data-label/data-info use :LI or :LO without reload
        const promoButtons = document.querySelectorAll('[data-info^="Promo:hero-"]');
        const suffix = user.logged_in ? 'LI' : 'LO';
        promoButtons.forEach(btn => {
            const oldLabel = btn.getAttribute('data-label') || '';
            const oldInfo = btn.getAttribute('data-info') || '';
            const newLabel = oldLabel.replace(/:L[IO]$/, ':' + suffix);
            const newInfo = oldInfo.replace(/:L[IO]$/, ':' + suffix);
            if (newLabel !== oldLabel) {
                btn.setAttribute('data-label', newLabel);
                btn.setAttribute('data-info', newInfo);
            }
        });
    }
}

export function* root() {
    yield all([
        takeEvery('user:login', login),
        takeEvery('user:logout', logout),
        takeEvery('user:logged-out', loggedOut),
        takeEvery('user:login-via-password', loginViaPassword),
        takeEvery('user:logout-home-login', logoutHomeLogin),
        fork(watchLOLI),
    ]);
}
